import { useGetCustomFieldVariables } from '@/hooks/useCustomFieldsForRole';
import { automationDataAccessTypeReadableNameMap } from '@payaca/helpers/automationHelper';
import { AutomationDataAccessType } from '@payaca/types/automationDataAccessTypes';
import { AutomationEntityTypes } from '@payaca/types/automationTypes';
import { automationEntityTypeActionVariablesMap } from '@payaca/types/automationVariableTypes';
import { useMemo } from 'react';

export const useActionVariablesForAutomationEntityType = (
  automationEntityType: AutomationEntityTypes
) => {
  const projectCustomFieldVariables =
    useGetCustomFieldVariables<AutomationDataAccessType>(
      'project.customField.value',
      automationDataAccessTypeReadableNameMap['project.customField.value'],
      { excludeTypes: ['user'] }
    );
  const companyCustomFieldVariables =
    useGetCustomFieldVariables<AutomationDataAccessType>(
      'account.customField.value',
      automationDataAccessTypeReadableNameMap['account.customField.value'],
      { role: 'account', excludeTypes: ['user'] }
    );

  // TODO: Apply some additional filtering here - not all variables should be available on default automations
  const variables = useMemo(() => {
    const availableVariables =
      automationEntityTypeActionVariablesMap[automationEntityType] || [];
    const fixedVariables = availableVariables.filter(
      (variable) => variable !== 'account.customField.value'
    );
    const variableNames: string[] = [...fixedVariables];
    const variableReadableNameMap: Record<string, string> = {};

    fixedVariables.forEach((variable) => {
      variableReadableNameMap[variable] =
        automationDataAccessTypeReadableNameMap[variable];
    });

    [
      ...(availableVariables.includes('project.customField.value')
        ? projectCustomFieldVariables
        : []),
      ...(availableVariables.includes('account.customField.value')
        ? companyCustomFieldVariables
        : []),
    ].forEach((variable) => {
      variableNames.push(variable.variableName);
      variableReadableNameMap[variable.variableName] = variable.displayName;
    });

    return { variableNames, variableReadableNameMap };
  }, [
    automationEntityType,
    projectCustomFieldVariables,
    companyCustomFieldVariables,
  ]);

  return {
    variables: variables.variableNames,
    variableReadableNameMap: variables.variableReadableNameMap,
  };
};
